<template>
    <div class="min-h-screen bg-gray-50">
        <!-- Container principal -->
        <div class="grid lg:grid-cols-2 min-h-screen">

            <!-- Colonne gauche - Illustration -->
            <div class="hidden lg:flex relative bg-gradient-to-br from-teal-600 to-teal-800"
                style="background-image: url('/bg.png'); background-size: cover; background-position: center;">
                <div class="absolute inset-0 bg-teal-900/60 backdrop-blur-[2px]"></div>

                <div class="relative z-10 flex flex-col items-center justify-center w-full p-12">
                    <!-- Logo ou titre de l'application -->
                    <div class="mb-8 text-center">
                        <h1 class="text-4xl font-bold text-white mb-2">Bienvenue</h1>
                        <p class="text-teal-100 text-lg">Gérez vos conversations en toute simplicité</p>
                    </div>

                    <!-- Illustration -->
                    <img src="@/assets/empty.svg" class="w-full max-w-lg" alt="Illustration de connexion" />

                </div>
            </div>

            <!-- Colonne droite - Formulaire -->
            <div class="flex items-center justify-center p-6 lg:p-12">
                <div class="w-full max-w-md">

                    <!-- En-tête du formulaire -->
                    <div class="text-center mb-8">
                        <!-- Logo mobile -->
                        <div class="lg:hidden mb-6">
                            <div
                                class="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl mx-auto flex items-center justify-center">
                                <i class="las la-comments text-3xl text-white"></i>
                            </div>
                        </div>

                        <h2 class="text-3xl font-bold text-gray-900 mb-2">
                            Connexion
                        </h2>
                        <p class="text-gray-600">
                            Accédez à votre espace de travail
                        </p>
                    </div>

                    <!-- Message d'erreur -->
                    <transition name="slide-down">
                        <div v-if="showMessage && !isLogin"
                            class="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg" role="alert">
                            <div class="flex items-start">
                                <i class="las la-exclamation-circle text-red-500 text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                                <div class="flex-1">
                                    <h3 class="text-sm font-semibold text-red-800 mb-1">Erreur de connexion</h3>
                                    <p class="text-sm text-red-700">{{ message }}</p>
                                </div>
                            </div>
                        </div>
                    </transition>

                    <!-- Formulaire -->
                    <form @submit.prevent="beforeSubmit" class="space-y-5">

                        <!-- Champ Email -->
                        <div>
                            <label for="email" class="block text-sm font-semibold text-gray-700 mb-2">
                                Adresse email
                            </label>
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i class="las la-envelope text-gray-400 text-lg"></i>
                                </div>
                                <input id="email" v-model="form.email" type="email" name="email" required
                                    autocomplete="email" placeholder="exemple@email.com" class="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 
                         focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         transition-all duration-200" />
                            </div>
                        </div>

                        <!-- Champ Mot de passe -->
                        <div>
                            <label for="password" class="block text-sm font-semibold text-gray-700 mb-2">
                                Mot de passe
                            </label>
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i class="las la-lock text-gray-400 text-lg"></i>
                                </div>
                                <input id="password" v-model="form.password" :type="showPassword ? 'text' : 'password'"
                                    name="password" required autocomplete="current-password" placeholder="••••••••"
                                    class="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         transition-all duration-200" />
                                <button type="button" @click="togglePassword"
                                    class="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors">
                                    <i :class="showPassword ? 'las la-eye-slash' : 'las la-eye'" class="text-lg">
                                    </i>
                                </button>
                            </div>
                        </div>

                        <!-- Mode de connexion -->
                        <div>
                            <label for="mode" class="block text-sm font-semibold text-gray-700 mb-2">
                                Mode de connexion
                            </label>
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <i class="las la-server text-gray-400 text-lg"></i>
                                </div>
                                <select id="mode" v-model="form.is_sso" name="is_sso" required class="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-lg text-gray-900 appearance-none
                         focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
                         transition-all duration-200 cursor-pointer">
                                    <option selected value="false">ALIBORA</option>
                                </select>
                                <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                                    <i class="las la-angle-down text-gray-400 text-lg"></i>
                                </div>
                            </div>
                        </div>

                        <!-- Options supplémentaires -->
                        <div class="flex items-center justify-between text-sm">
                            <label class="flex items-center cursor-pointer group">
                                <input type="checkbox"
                                    class="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer" />
                                <span class="ml-2 text-gray-600 group-hover:text-gray-900 transition-colors">
                                    Se souvenir de moi
                                </span>
                            </label>
                            <a href="#" class="text-teal-600 hover:text-teal-700 font-medium transition-colors">
                                Mot de passe oublié ?
                            </a>
                        </div>

                        <!-- Bouton de connexion -->
                        <button type="submit" :disabled="isLoading" :class="[
                            'w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 transform',
                            isLoading
                                ? 'bg-teal-400 cursor-not-allowed'
                                : 'bg-teal-600 hover:bg-teal-700 hover:shadow-lg active:scale-[0.98]'
                        ]">
                            <span v-if="isLoading" class="flex items-center justify-center">
                                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="4"></circle>
                                    <path class="opacity-75" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                    </path>
                                </svg>
                                Connexion en cours...
                            </span>
                            <span v-else class="flex items-center justify-center">
                                <i class="las la-sign-in-alt mr-2 text-lg"></i>
                                Se connecter
                            </span>
                        </button>
                    </form>

                    <!-- Pied de page -->
                    <div class="mt-8 text-center">
                        <p class="text-sm text-gray-600">
                            Besoin d'aide ?
                            <a href="#" class="text-teal-600 hover:text-teal-700 font-medium transition-colors">
                                Contactez le support
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex"
import { mapFields } from "vuex-map-fields"

export default {
    name: "LoginPage",
    data() {
        return {
            form: {
                email: null,
                password: null,
                is_sso: "true",
            },
            showPassword: false,
        }
    },
    computed: {
        ...mapFields("auth/login", {
            isLoading: "isLoading",
            item: "item",
            isLogin: "isLogin",
            showMessage: "showMessage",
            message: "message"
        })
    },
    methods: {
        ...mapActions({
            handleSubmit: "auth/login/login",
        }),

        beforeSubmit() {
            // Validation basique
            if (!this.form.email || !this.form.password) {
                console.error("Les champs email et mot de passe sont obligatoires")
                return;
            }

            if (!this.form.is_sso) {
                console.error("Veuillez choisir un mode de connexion")
                return;
            }

            // Soumission du formulaire
            this.handleSubmit(this.form)
        },

        togglePassword() {
            this.showPassword = !this.showPassword;
        },
    },
}
</script>

<style scoped>
/* Animation pour le message d'erreur */
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease-out;
}

.slide-down-enter-from {
    opacity: 0;
    transform: translateY(-10px);
}

.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Personnalisation de la scrollbar si nécessaire */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: transparent;
}

::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}
</style>